// src/components/CustomFunnel/DemoRequestPage.tsx

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Loader2,
  Lock,
  Clock,
  Users,
  Play,
  BarChart2,
  Zap,
  CreditCard,
  Package,
} from "lucide-react";
import { useRegisterWebinarMutation } from "../../app/api/customFunnelApi";

const DEMO_PIXEL = "1200477918904052";

// ─── Static content ────────────────────────────────────────────────────────────

const TRUST_INDICATORS = [
  "Works for any business — products, services, courses, coaching",
  "Access sent to your email & WhatsApp within 60 seconds",
  "Free to access. No credit card. No obligation.",
];

const AGENDA_ITEMS = [
  {
    Icon: Play,
    title: "Your Business Makes Money While You Sleep",
    description:
      "See a complete sales system run without human input — a lead arrives, gets nurtured automatically, reaches a sales page, and pays. Whether you sell a course, a service, or a physical product, this is the outcome you'll walk away understanding how to build.",
  },
  {
    Icon: Zap,
    title: "You Write the Words. The System Does the Rest.",
    description:
      "You don't need to build anything from scratch. Write your offer in plain text — the system generates your sales page, your opt-in form, and your follow-up emails from templates. You review, you approve, it goes live. No developer, no designer, no months of waiting.",
  },
  {
    Icon: CreditCard,
    title: "Every Payment Collected Automatically — Into Your Bank",
    description:
      "See how Paystack is embedded directly into your sales page so customers pay in full or in instalments and the money lands in your Nigerian bank account automatically. No chasing invoices, no manual confirmations, no payment links on WhatsApp.",
  },
  {
    Icon: BarChart2,
    title: "You Stay in Full Control — Without Doing Everything Yourself",
    description:
      "Watch how the dashboard gives you a real-time view of every lead, every email opened, every naira earned — across all your products at once. You know exactly what's working and where to focus. The system runs itself; you make the decisions.",
  },
  {
    Icon: Package,
    title: "Your Product Delivered Instantly — Even at 3am",
    description:
      "Digital download, course access, booking confirmation, or service brief — the moment payment clears, your customer gets exactly what they paid for, automatically. No support tickets. No 'please check your email' messages. Just a seamless experience that makes you look professional.",
  },
];

const BUSINESS_TYPES = [
  "Online Courses",
  "Coaching & Consulting",
  "Digital Products",
  "Service Businesses",
  "E-commerce",
  "Training Programmes",
  "Memberships",
  "Agency Services",
];

// ─── Form types ────────────────────────────────────────────────────────────────

interface FormState {
  firstName: string;
  email: string;
  whatsapp: string;
}

interface FormErrors {
  firstName?: string;
  email?: string;
  whatsapp?: string;
  submit?: string;
}

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
const isValidPhone = (s: string) =>
  /^[+\d\s\-()\u202A-\u202C]{7,}$/.test(s.trim());

// ─── Component ─────────────────────────────────────────────────────────────────

const DemoRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [registerWebinar, { isLoading }] = useRegisterWebinarMutation();
  const formRef = useRef<HTMLDivElement | null>(null);

  const [form, setForm] = useState<FormState>({
    firstName: "",
    email: "",
    whatsapp: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // ── Track ViewContent on mount — fires only on DEMO_PIXEL ───────────────────
  useEffect(() => {
    window.fbq?.("trackSingle", DEMO_PIXEL, "ViewContent", {
      content_name: "Demo Request Page",
    });
  }, []);

  const setField = (key: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    if (errors.submit) setErrors((e) => ({ ...e, submit: undefined }));
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!isValidEmail(form.email)) e.email = "Enter a valid email address";
    if (!isValidPhone(form.whatsapp))
      e.whatsapp = "Enter a valid WhatsApp number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    try {
      const res = await registerWebinar({
        firstName: form.firstName.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
      }).unwrap();

      if (res.success && res.uniqueId) {
        // ── Fire Lead event on successful registration — DEMO_PIXEL only ──────
        window.fbq?.("trackSingle", DEMO_PIXEL, "Lead", {
          content_name: "Demo Registration",
          content_category: "Sales System Webinar",
        });
        navigate(`/sales-funnel/webinar/${res.uniqueId}`);
      } else {
        setErrors((e) => ({
          ...e,
          submit: res.message || "Registration failed. Please try again.",
        }));
      }
    } catch (err: any) {
      setErrors((e) => ({
        ...e,
        submit:
          err?.data?.message ||
          err?.message ||
          "Something went wrong. Please try again.",
      }));
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const inputCls = (hasError: boolean) =>
    [
      "w-full px-3 py-2.5 text-sm border-2 rounded-lg outline-none transition",
      "focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
      "disabled:opacity-60 disabled:cursor-not-allowed",
      hasError ? "border-red-400 bg-red-50" : "border-gray-300 bg-white",
    ].join(" ");

  return (
    <div className="min-h-screen bg-white">

      {/* ════ URGENCY BANNER ════ */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 py-3 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 text-white">
            <Users size={18} className="flex-shrink-0" />
            <p className="text-sm sm:text-base font-bold">
              ⚡ Only 4 Setup Slots Remaining This Month — 6 of 10 Already Taken
            </p>
          </div>
        </div>
      </div>

      {/* ════ HERO ════ */}
      <section className="bg-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Headline */}
          <div className="text-center mb-8 max-w-4xl mx-auto">
            <div className="inline-block mb-4 px-4 py-1.5 bg-orange-50 border border-orange-200 rounded-full">
              <p className="text-sm text-orange-700 font-semibold">
                For coaches, course creators, service providers & product sellers
              </p>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
              Your Business Is Losing Money Every Day{" "}
              <span className="text-orange-500">
                It Doesn't Have a System That Sells for You
              </span>
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Most Nigerian businesses have a great offer but no machine to sell
              it. Leads fall through. Follow-up doesn't happen. Payments are
              collected manually. In 32 minutes, you'll see exactly how to fix
              all of it — for any business, without writing a single line of
              code.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">

            {/* ── LEFT ── */}
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden shadow-lg border-2 border-gray-200">
                <img
                  src="/images/dashboard.png"
                  alt="Launch Kit Naija Sales System Dashboard"
                  className="w-full object-cover object-top"
                  style={{ maxHeight: 360 }}
                />
              </div>

              {/* Business types */}
              <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-100">
                <p className="text-sm font-bold text-blue-800 uppercase tracking-wide mb-3">
                  Works for every type of business
                </p>
                <div className="flex flex-wrap gap-2">
                  {BUSINESS_TYPES.map((type) => (
                    <span
                      key={type}
                      className="bg-white border border-blue-200 text-blue-800 text-xs font-semibold px-3 py-1.5 rounded-full"
                    >
                      ✓ {type}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-700 mt-4 leading-relaxed">
                  Whether you sell a ₦5,000 PDF, a ₦500,000 consulting retainer,
                  or a ₦50,000 online course — the system adapts to your offer.
                  You write the text. It builds the machine.
                </p>
              </div>

              {/* What you get */}
              <div className="bg-white rounded-xl shadow-md p-5 space-y-3 border-2 border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock size={20} className="text-orange-500" />
                  What You're Getting Access To
                </h3>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock size={18} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">
                      32-minute platform walkthrough
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Real system. Real data. No slides, no theory.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">
                      Personal access link — yours to keep
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Sent to your email & WhatsApp instantly. Rewatch any time.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">
                      See the exact system your business needs
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Lead capture → automated follow-up → payment → delivery. All automated.
                    </p>
                  </div>
                </div>
              </div>

              {/* Urgency card */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3 text-white">
                  <Users size={22} className="flex-shrink-0" />
                  <p className="font-bold text-sm sm:text-base">
                    ⚠️ We take a maximum of 10 clients per month to guarantee
                    quality. 6 slots are already gone. Access is free — but
                    availability isn't.
                  </p>
                </div>
              </div>
            </div>

            {/* ── RIGHT: sticky form ── */}
            <div className="lg:sticky lg:top-16" ref={formRef}>
              <div className="bg-white rounded-xl shadow-2xl overflow-hidden border-2 border-orange-200">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 sm:px-8 py-5 text-center">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    SEE HOW IT WORKS — FREE
                  </h2>
                  <p className="text-orange-100 text-xs sm:text-sm mt-1">
                    Your personal access link goes to your email & WhatsApp instantly
                  </p>
                </div>

                <div className="p-5 sm:p-6 bg-gradient-to-b from-orange-50 to-white">
                  <form onSubmit={handleSubmit} noValidate className="space-y-4">

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.firstName}
                        onChange={(e) => setField("firstName", e.target.value)}
                        placeholder="Chidi"
                        disabled={isLoading}
                        className={inputCls(!!errors.firstName)}
                      />
                      {errors.firstName && (
                        <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        placeholder="chidi@yourbiz.com"
                        disabled={isLoading}
                        className={inputCls(!!errors.email)}
                      />
                      {errors.email && (
                        <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                        WhatsApp Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={form.whatsapp}
                        onChange={(e) => setField("whatsapp", e.target.value)}
                        placeholder="+234 812 345 6789"
                        disabled={isLoading}
                        className={inputCls(!!errors.whatsapp)}
                      />
                      {errors.whatsapp && (
                        <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-base sm:text-lg py-3.5 rounded-lg shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 size={20} className="animate-spin" />
                          SENDING YOUR ACCESS LINK…
                        </span>
                      ) : (
                        "SHOW ME HOW IT WORKS →"
                      )}
                    </button>

                    {errors.submit && (
                      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
                        <span className="text-red-500 flex-shrink-0 mt-0.5">⚠️</span>
                        <p className="text-red-700 text-sm">{errors.submit}</p>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1 pt-1">
                      <Lock size={12} />
                      100% free. No credit card. No spam. Unsubscribe any time.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ TRUST INDICATORS ════ */}
      <section className="py-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-y border-blue-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TRUST_INDICATORS.map((text, i) => (
              <div
                key={i}
                className="flex items-center gap-3 justify-center md:justify-start bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-blue-100"
              >
                <CheckCircle size={22} className="text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ AGENDA ════ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            What You'll Discover in 32 Minutes
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Not a pitch. Not a tutorial. A live look at exactly how your
            business can run — from the first lead to the final payment —
            without you being involved in every step.
          </p>

          <div className="space-y-4">
            {AGENDA_ITEMS.map(({ Icon, title, description }, i) => (
              <div
                key={i}
                className="flex items-start gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-blue-100"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={18} className="text-orange-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={scrollToForm}
              className="inline-block bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg px-10 py-4 rounded-lg shadow-lg transition transform hover:scale-105"
            >
              SHOW ME HOW IT WORKS →
            </button>
          </div>
        </div>
      </section>

      {/* ════ FINAL CTA ════ */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Every Day Without a System Is a Day of Lost Revenue
          </h2>
          <p className="text-xl text-orange-100 mb-6 max-w-2xl mx-auto">
            You have a great offer. What you don't have yet is the machine to
            sell it consistently — while you focus on actually delivering.
            That's what this 32 minutes will show you.
          </p>
          <p className="text-lg font-bold text-white mb-8">
            ⚠️ Only 4 Setup Slots Left This Month — Access the Demo Before They're Gone
          </p>
          <button
            onClick={scrollToForm}
            className="inline-block bg-white hover:bg-gray-100 text-orange-600 font-bold text-lg px-12 py-5 rounded-lg shadow-lg transition transform hover:scale-105"
          >
            SHOW ME HOW IT WORKS — FREE
          </button>
          <p className="text-orange-200 text-xs mt-4">
            Zero monthly fees · You own everything · Live in 7 days
          </p>
        </div>
      </section>

      {/* ════ FOOTER ════ */}
      <footer className="bg-gray-900 text-gray-300 py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Brand + nav links */}
          <div className="text-center mb-6">
            <p className="text-white font-bold text-base mb-3">Launch Kit Naija</p>
            <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link
                to="/sales-funnel/sales"
                className="text-gray-400 hover:text-white transition underline underline-offset-2"
              >
                ← Back to Sales Page
              </Link>
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-white transition underline underline-offset-2"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white transition underline underline-offset-2"
              >
                Terms of Service
              </Link>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-700 my-6" />

          {/* Meta / Facebook disclaimer */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 mb-6">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">
              Advertising Disclosure
            </p>
            <p className="text-xs text-gray-500 leading-relaxed">
              This page may have been reached via a paid advertisement on
              Facebook or Instagram, platforms operated by Meta Platforms, Inc.
              Launch Kit Naija uses the Meta Pixel to measure the effectiveness
              of its advertising campaigns and to show relevant ads to people
              who have visited this site. The Meta Pixel collects data such as
              page views and form submissions in accordance with Meta's{" "}
              <a
                href="https://www.facebook.com/privacy/policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white underline underline-offset-2 transition"
              >
                Data Policy
              </a>
              . By submitting the form on this page you agree to receive
              communications from Launch Kit Naija. You may unsubscribe at any
              time. This site is not affiliated with, endorsed by, or sponsored
              by Meta Platforms, Inc. Results described on this page are
              illustrative and not guaranteed. Individual outcomes will vary
              based on effort, experience, and market conditions. Please read
              our{" "}
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-white underline underline-offset-2 transition"
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white underline underline-offset-2 transition"
              >
                Terms of Service
              </Link>{" "}
              for full details on how your data is collected, stored, and used.
            </p>
          </div>

          {/* Copyright */}
          <p className="text-xs text-gray-600 text-center">
            © {new Date().getFullYear()} Launch Kit Naija · Adeolu Adegbemi. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DemoRequestPage;